/**
 * 圖片預處理相關 API
 */

import { POLLING } from '@/config/constants';
import { apiClient, ApiError } from './client';
import type { TaskResponse } from '@/types/api';
import type { Line, Rectangle } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

/** 預處理請求參數 */
export interface PreprocessParams {
  image: File;
  /** 是否移除文字和標線（呼叫 Gemini API） */
  removeText?: boolean;
  /** 是否偵測尺寸標註（呼叫 Cloud Vision API） */
  detectDimensions?: boolean;
  referenceLine?: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    lengthMeters: number;
  };
  coverRectangles?: Rectangle[];
  blackLines?: Line[];
}

/** 後端偵測到的尺寸標註格式 */
interface BackendDetectedDimension {
  id: number;
  value: number;
  unit: string;
  value_meters: number;
  line: {
    start: { x: number; y: number };
    end: { x: number; y: number };
  };
  label_position: { x: number; y: number };
  confidence: number;
  orientation: 'horizontal' | 'vertical';
}

/** 預處理回應（後端任務結果格式） */
export interface PreprocessResponse {
  status: string;
  data: {
    image_base64: string;
    original_size: { width: number; height: number };
    output_size: { width: number; height: number };
    detected_elements: {
      wall_count: number;
      pillar_count: number;
      pillar_positions: Array<{ x: number; y: number; width: number; height: number }>;
      door_count: number;
      door_positions: Array<{ x: number; y: number; width: number; height: number }>;
    };
    detected_dimensions: BackendDetectedDimension[];
    processing_time_ms: number;
  };
  timestamp: string;
}

/** 進度回呼函式 */
export type ProgressCallback = (progress: number, message: string) => void;

/** 前端使用的尺寸標註格式 */
export interface FrontendDetectedDimension {
  id: number;
  value: number;
  unit: string;
  valueMeters: number;
  line: {
    start: { x: number; y: number };
    end: { x: number; y: number };
  };
  position: { x: number; y: number }; // 標籤位置（對應後端的 label_position）
  confidence: number;
  orientation: 'horizontal' | 'vertical';
}

/** 預處理結果（包含圖片和偵測到的尺寸） */
export interface PreprocessResult {
  imageBase64: string;
  detectedDimensions: FrontendDetectedDimension[];
}

// ========================================================================
// 預處理 API
// ========================================================================

/**
 * 啟動圖片預處理任務（Gemini 清理 + 可選尺寸偵測）
 */
export async function startPreprocess(params: PreprocessParams): Promise<string> {
  const formData = new FormData();

  // 圖片
  formData.append('image', params.image);

  // 是否偵測尺寸（預設 true）
  formData.append('detect_dimensions', String(params.detectDimensions ?? true));

  // 參考線
  if (params.referenceLine) {
    formData.append('reference_line', JSON.stringify(params.referenceLine));
  }

  // 覆蓋矩形
  if (params.coverRectangles && params.coverRectangles.length > 0) {
    formData.append('cover_rectangles', JSON.stringify(params.coverRectangles));
  }

  // 塗黑線條
  if (params.blackLines && params.blackLines.length > 0) {
    formData.append('black_lines', JSON.stringify(params.blackLines));
  }

  const response = await apiClient.postForm<{ task_id: string }>('/api/v1/preprocess-floorplan/async', {
    body: formData,
  });

  return response.task_id;
}

/**
 * 查詢預處理任務狀態
 */
export async function getPreprocessStatus(taskId: string): Promise<TaskResponse> {
  return apiClient.get<TaskResponse>(`/api/v1/tasks/${taskId}`);
}

/**
 * 執行預處理並等待結果
 */
export async function preprocessImage(
  params: PreprocessParams,
  onProgress?: ProgressCallback
): Promise<PreprocessResult> {
  // 啟動非同步任務
  onProgress?.(0, '提交預處理請求...');
  const taskId = await startPreprocess(params);

  // 輪詢等待結果
  const startTime = Date.now();
  let lastProgress = 0;

  while (true) {
    // 檢查超時
    if (Date.now() - startTime > POLLING.TIMEOUT) {
      throw new ApiError(408, '預處理超時，請稍後再試');
    }

    // 查詢狀態
    const status = await getPreprocessStatus(taskId);

    // 更新進度
    const progress = status.progress ?? lastProgress;
    if (progress !== lastProgress) {
      lastProgress = progress;
      onProgress?.(progress, status.message ?? '處理中...');
    }

    // 檢查結果
    switch (status.status) {
      case 'completed':
        onProgress?.(100, '預處理完成');
        const result = status.result as PreprocessResponse;
        if (!result?.data?.image_base64) {
          throw new ApiError(500, '預處理結果無效');
        }
        return {
          imageBase64: result.data.image_base64,
          detectedDimensions: mapBackendDimensions(result.data.detected_dimensions ?? []),
        };

      case 'failed':
        throw new ApiError(500, status.error ?? '預處理失敗');

      case 'pending':
      case 'processing':
        // 繼續等待
        await sleep(POLLING.INTERVAL);
        break;
    }
  }
}

// ========================================================================
// 尺寸偵測 API
// ========================================================================

/** 後端尺寸偵測回應格式 */
interface DetectDimensionsResponse {
  status: string;
  data: {
    dimensions: BackendDetectedDimension[];
    image_size: { width: number; height: number };
    ocr_mode: string;
  };
  processing_time_ms: number;
}

/**
 * 僅偵測圖片中的尺寸標註（不做 Gemini 清理）
 * 返回格式化的尺寸資料
 */
export async function detectDimensionsOnly(image: File): Promise<FrontendDetectedDimension[]> {
  const formData = new FormData();
  formData.append('image', image);

  const response = await apiClient.postForm<DetectDimensionsResponse>(
    '/api/v1/detect-dimensions',
    {
      body: formData,
      timeout: 60000, // 1 分鐘
    }
  );

  if (response.status !== 'success') {
    throw new ApiError(500, '尺寸偵測失敗');
  }

  // 映射後端格式到前端格式
  return mapBackendDimensions(response.data?.dimensions ?? []);
}

/**
 * 將後端尺寸格式映射到前端格式
 */
function mapBackendDimensions(dimensions: BackendDetectedDimension[]): FrontendDetectedDimension[] {
  return dimensions.map((dim) => ({
    id: dim.id,
    value: dim.value,
    unit: dim.unit,
    valueMeters: dim.value_meters,
    line: dim.line,
    position: dim.label_position,
    confidence: dim.confidence,
    orientation: dim.orientation,
  }));
}

// ========================================================================
// 輔助函式
// ========================================================================

/**
 * 延遲函式
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
